// @ts-nocheck
import { useCallback } from 'react';
import { Box, Container, Modal, Text, Title } from '@/components/ui';
import {
  DashboardProvider,
  DashboardGrid,
  DashboardToolbar,
  WidgetPicker,
  WidgetSettingsModal,
  WidgetFiltersModal,
  ContainerChildSettingsModal,
  useDashboardContext,
} from '../../../components/advanced/dashboard';
import {
  useDashboardStore,
  selectIsPickerOpen,
  selectSettingsModalWidget,
  selectSettingsModalContainerChild,
  selectFiltersModalWidget,
  selectExpandedWidget,
} from '../../../stores/dashboardStore';
import { getMockDashboardData } from './mockData';
import { FadeIn } from '../../../components/animations';
import { getWidget } from '../../../components/advanced/dashboard/registry';

// ============================================================================
// DashboardContent Component - Uses dashboard context for refresh
// ============================================================================

function DashboardContent() {
  const { data: globalData, refetch } = useDashboardContext();

  const isPickerOpen = useDashboardStore(selectIsPickerOpen);
  const closePicker = useDashboardStore((state) => state.closePicker);
  const settingsModalWidget = useDashboardStore(selectSettingsModalWidget);
  const settingsModalContainerChild = useDashboardStore(selectSettingsModalContainerChild);
  const filtersModalWidget = useDashboardStore(selectFiltersModalWidget);
  const expandedWidget = useDashboardStore(selectExpandedWidget);
  const closeSettingsModal = useDashboardStore((state) => state.closeSettingsModal);
  const closeContainerChildSettingsModal = useDashboardStore(
    (state) => state.closeContainerChildSettingsModal
  );
  const closeFiltersModal = useDashboardStore((state) => state.closeFiltersModal);
  const collapseExpandedWidget = useDashboardStore((state) => state.collapseExpandedWidget);
  const widgets = useDashboardStore((state) => state.widgets);

  // Handle global refresh
  const handleGlobalRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Get expanded widget info
  const expandedWidgetInstance = expandedWidget
    ? widgets.find((w) => w.instanceId === expandedWidget)
    : null;
  const expandedWidgetDef = expandedWidgetInstance
    ? getWidget(expandedWidgetInstance.widgetId)
    : null;

  return (
    <Container fluid p="md">
      {/* Page Header */}
      <FadeIn>
        <Box mb="lg">
          <Title order={2} mb="xs">
            Dashboard Widgets Demo
          </Title>
          <Text c="dimmed" size="sm">
            A customizable widget dashboard with drag-and-drop reordering, responsive grid layout,
            and persistent settings. Click &quot;Edit&quot; to rearrange widgets or &quot;Add Widget&quot; to add new ones.
          </Text>
        </Box>
      </FadeIn>

      {/* Dashboard Toolbar with Global Refresh */}
      <DashboardToolbar
        title="My Dashboard"
        showWidgetCount
        onRefresh={handleGlobalRefresh}
      />

      {/* Dashboard Grid with Widgets */}
      <DashboardGrid globalData={globalData || undefined} />

      {/* Widget Picker Modal */}
      <WidgetPicker opened={isPickerOpen} onClose={closePicker} />

      {/* Widget Settings Modal */}
      <WidgetSettingsModal
        opened={!!settingsModalWidget}
        onClose={closeSettingsModal}
      />

      {/* Widget Filters Modal */}
      <WidgetFiltersModal
        opened={!!filtersModalWidget}
        onClose={closeFiltersModal}
      />

      {/* Container Child Settings Modal */}
      <ContainerChildSettingsModal
        opened={!!settingsModalContainerChild}
        onClose={closeContainerChildSettingsModal}
      />

      {/* Expanded Widget Modal */}
      {expandedWidgetDef && expandedWidgetInstance && (
        <Modal
          opened={!!expandedWidget}
          onClose={collapseExpandedWidget}
          title={expandedWidgetInstance.settings?.customTitle || expandedWidgetDef.name}
          size="xl"
          fullScreen
        >
          <Box p="md" style={{ height: 'calc(100vh - 100px)', overflow: 'auto' }}>
            <expandedWidgetDef.component
              widgetId={expandedWidgetInstance.widgetId}
              instanceId={expandedWidgetInstance.instanceId}
              isCollapsed={false}
              globalData={globalData || undefined}
              filters={expandedWidgetInstance.filters}
            />
          </Box>
        </Modal>
      )}
    </Container>
  );
}

// ============================================================================
// DashboardPage Component
// ============================================================================

export default function DashboardPage() {
  // Get mock data for the demo
  const mockData = getMockDashboardData();

  return (
    <DashboardProvider initialData={mockData}>
      <DashboardContent />
    </DashboardProvider>
  );
}
